/**
 * Generic page-level cache — survives navigation, cleared on hard refresh.
 * Usage: call `createPageCache<MyShape>()` at module level in a view file.
 */
export function createPageCache<T extends object>() {
  type Entry = T & { startDate?: string; endDate?: string };
  const cache = new Map<string, Entry>();

  return {
    get(key: string): Entry | undefined {
      return cache.get(key);
    },

    /** Returns true if cache exists for this key AND the dates match. */
    isHit(key: string, startDate: string, endDate: string): boolean {
      const c = cache.get(key);
      return !!c && c.startDate === startDate && c.endDate === endDate;
    },

    /** Merges a partial update into the existing cache entry. */
    patch(key: string, data: Partial<Entry>): void {
      cache.set(key, { ...cache.get(key)!, ...data });
    },

    /** Clears the entry for a given key (e.g. on place change). */
    invalidate(key: string): void {
      cache.delete(key);
    },
  };
}
